<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAll" :disabled="!loaded"></b-form-checkbox>
        <span @click="loaded ? (checkedAll = !checkedAll) : null">Czas w Las</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;" :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.czaswlas-pointer-cursor),
:global(.czaswlas-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import Photo from '@/ts/Photo';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasScatterplotLayer } from '@/ts/MapLasScatterplotLayer';
import lightGallery from 'lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgAutoplay from 'lightgallery/plugins/autoplay';

interface CzasWLasResponsePlace {
  id: number;
  name: string;
  lat: number | string;
  lon: number | string;
  category: number;
  link: string;
  waypoints: string;
  images: { id?: number; full: string; thumb: string }[];
  is_active: boolean;
}

interface CzasWLasBinaryData {
  length: number;
  attributes: {
    getPosition: { value: Float32Array; size: number };
    getFillColor: { value: Uint8Array; size: number };
    getLineColor: { value: Uint8Array; size: number };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface CzasWLasPreparedData {
  data: CzasWLasBinaryData;
  results: CzasWLasResponsePlace[];
}

interface CzasWLasPopupPlace {
  id: number;
  name: string;
  lat: number;
  lon: number;
  link: string;
  photos: Photo[];
}

interface CzasWLasCardInstanceState {
  czaswlasResult: CzasWLasResponsePlace[] | null;
  czaswlasLayerResults: CzasWLasResponsePlace[];
  czaswlasLayer: MapLasScatterplotLayer | null;
  deckOverlay: DeckOverlay | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
  hoveredWaypoints: L.Polyline[];
  hoveredPlaceIndex: number | null;
}

function colorForCategory(category: number): [number, number, number] {
  if ([8].includes(category)) return [38, 120, 252];
  if ([4, 7, 12, 13, 14, 16].includes(category)) return [0, 128, 0];
  if ([2, 3, 15, 20].includes(category)) return [16, 194, 63];
  if ([1, 5, 11, 21].includes(category)) return [165, 42, 42];
  if ([17].includes(category)) return [227, 64, 205];
  if ([0, 9].includes(category)) return [114, 128, 219];
  if ([6, 10, 24, 25, 26, 27].includes(category)) return [0, 0, 0];
  return [255, 165, 0];
}

function createEmptyBinaryData(): CzasWLasBinaryData {
  return {
    length: 0,
    attributes: {
      getPosition: { value: new Float32Array(0), size: 2 },
      getFillColor: { value: new Uint8Array(0), size: 4 },
      getLineColor: { value: new Uint8Array(0), size: 4 },
      getFilterValue: { value: new Float32Array(0), size: 1 },
    },
  };
}

declare module 'vue' {
  interface ComponentCustomProperties extends CzasWLasCardInstanceState {}
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAll: false,
      expanded: false,
      active: true,
      inactive: false,
      loaded: false,
      loading: false,
    };
  },
  created() {
    this.czaswlasResult = null;
    this.czaswlasLayerResults = [];
    this.czaswlasLayer = null;
    this.deckOverlay = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    this.hoveredWaypoints = [];
    this.hoveredPlaceIndex = null;
    EventBus.$on('downloadExternalData', () => this.downloadPlacesCzasWLas());
    EventBus.$on('toggleCzasWLas', () => {
      if (this.czaswlasResult) {
        this.checkedAll = !this.checkedAll;
      }
    });
    EventBus.$on('selectedAreaChanged', () => this.onSelectedAreaChanged());
  },
  beforeUnmount() {
    this.clearHoveredWaypoints();
    if (window.mainmap && this.mapMouseMoveHandler) {
      window.mainmap.off('mousemove', this.mapMouseMoveHandler);
      this.mapMouseMoveHandler = null;
    }
    if (window.mainmap && this.mapMouseOutHandler) {
      window.mainmap.off('mouseout', this.mapMouseOutHandler);
      this.mapMouseOutHandler = null;
    }
    if (window.mainmap && this.mapClickHandler) {
      window.mainmap.off('click', this.mapClickHandler);
      this.mapClickHandler = null;
    }
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('czaswlas-pointer-cursor');
    }
    if (this.deckOverlay && window.mainmap) {
      window.mainmap.removeLayer(this.deckOverlay);
      this.deckOverlay = null;
    }
  },
  methods: {
    isPointInSelectedArea(lat: number, lon: number) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
      return lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    },
    createBinaryData(results: CzasWLasResponsePlace[]): CzasWLasPreparedData {
      const visibleResults = results.filter((place) => this.isPointInSelectedArea(Number(place.lat), Number(place.lon)));
      const positions = new Float32Array(visibleResults.length * 2);
      const fillColors = new Uint8Array(visibleResults.length * 4);
      const lineColors = new Uint8Array(visibleResults.length * 4);
      const filterValues = new Float32Array(visibleResults.length);

      visibleResults.forEach((place, index) => {
        const color = colorForCategory(place.category);
        positions[index * 2] = Number(place.lon);
        positions[index * 2 + 1] = Number(place.lat);
        fillColors.set([color[0], color[1], color[2], 51], index * 4);
        lineColors.set([color[0], color[1], color[2], 255], index * 4);
        filterValues[index] = place.is_active ? 1 : 0;
      });

      return {
        results: visibleResults,
        data: {
          length: visibleResults.length,
          attributes: {
            getPosition: { value: positions, size: 2 },
            getFillColor: { value: fillColors, size: 4 },
            getLineColor: { value: lineColors, size: 4 },
            getFilterValue: { value: filterValues, size: 1 },
          },
        },
      };
    },
    ensureDeckOverlay() {
      if (!window.mainmap) return;
      if (!this.deckOverlay) {
        this.deckOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.deckOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => this.onMapMouseOut();
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    onMapMouseMove(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      const index = this.checkedAll && this.czaswlasLayer ? this.czaswlasLayer.pick(lat, lng) : null;
      window.mainmap.getContainer().classList.toggle('czaswlas-pointer-cursor', index !== null);
      if (index === this.hoveredPlaceIndex) return;

      this.clearHoveredWaypoints();
      if (index === null) return;

      const responsePlace = this.czaswlasLayerResults[index];
      if (!responsePlace || !responsePlace.waypoints) return;

      let polylines: L.Polyline[];
      try {
        const waypoints = JSON.parse(responsePlace.waypoints) as L.LatLngExpression[][];
        polylines = this.createWayPointsPolylines(waypoints);
      } catch {
        return;
      }
      this.hoveredPlaceIndex = index;
      for (const polyline of polylines) {
        polyline.addTo(window.mainmap);
        polyline.bringToFront();
        this.hoveredWaypoints.push(polyline);
      }
    },
    onMapMouseOut() {
      this.clearHoveredWaypoints();
      if (window.mainmap) {
        window.mainmap.getContainer().classList.remove('czaswlas-pointer-cursor');
      }
    },
    clearHoveredWaypoints() {
      if (window.mainmap) {
        for (const polyline of this.hoveredWaypoints) {
          polyline.removeFrom(window.mainmap);
        }
      }
      this.hoveredWaypoints = [];
      this.hoveredPlaceIndex = null;
    },
    onMapClick(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      if (!this.checkedAll || !this.czaswlasLayer) return;
      const index = this.czaswlasLayer.pick(lat, lng);
      if (index === null) return;
      const responsePlace = this.czaswlasLayerResults[index];
      if (!responsePlace) return;
      const place = this.createCzasWLasPopupPlace(responsePlace);

      window.mainmap.openPopup(this.createPopupContent(place), [place.lat, place.lon]);
    },
    createWayPointsPolylines(waypoints: L.LatLngExpression[][]): L.Polyline[] {
      const polylines: L.Polyline[] = [];
      for (const segment of waypoints) {
        const polyline = new L.Polyline(segment, {
          color: 'red',
          weight: 5,
          opacity: 1,
          smoothFactor: 1,
        });
        polylines.push(polyline);
      }
      return polylines;
    },
    createCzasWLasPopupPlace(responsePlace: CzasWLasResponsePlace): CzasWLasPopupPlace {
      return {
        id: responsePlace.id,
        name: responsePlace.name,
        lat: this.roundNumber(Number(responsePlace.lat), 6),
        lon: this.roundNumber(Number(responsePlace.lon), 6),
        link: `https://czaswlas.pl${responsePlace.link}`,
        photos: responsePlace.images.map((responsePhoto, index) => new Photo(
          responsePhoto.id ?? index,
          'Photo',
          '',
          responsePhoto.thumb,
          null,
          window.mediaHost + 'czaswlas/' + responsePhoto.full,
          window.mediaHost + 'czaswlas/' + responsePhoto.full,
          window.mediaHost + 'czaswlas/' + responsePhoto.thumb,
          false,
          1,
          false,
        )),
      };
    },
    createPopupContent(place: CzasWLasPopupPlace): HTMLElement {
      const popupContent = document.createElement('div');
      popupContent.style.cssText = 'min-width: 200px; max-width: 280px; box-sizing: border-box; overflow-wrap: anywhere;';
      const header = document.createElement('div');
      header.style.cssText = 'margin-bottom: 5px; overflow-wrap: anywhere;';
      const coords = `${place.lat},${place.lon}`;
      const mapLink = document.createElement('a');
      mapLink.href = `https://maps.google.com/maps?q=${coords}`;
      mapLink.target = '_blank';
      mapLink.rel = 'noopener noreferrer';

      if (window.isDesktop) {
        header.append(document.createTextNode(`${place.id} `));
        const name = document.createElement('a');
        name.href = place.link;
        name.target = '_blank';
        name.rel = 'noopener noreferrer';
        name.textContent = place.name;
        const boldName = document.createElement('b');
        boldName.append(name);
        mapLink.textContent = coords;
        header.append(boldName, document.createTextNode(' '), mapLink);
      } else {
        const boldName = document.createElement('b');
        boldName.textContent = place.name;
        const locationIcon = document.createElement('img');
        locationIcon.src = this.icons.locationBlack;
        locationIcon.style.height = '12px';
        locationIcon.style.cursor = 'pointer';
        mapLink.append(locationIcon);
        header.append(boldName, document.createTextNode(' '), mapLink);
      }

      const rightSide = document.createElement('span');
      rightSide.style.cssText = 'float: right; margin-right: 16px;';
      const blankIcon = document.createElement('img');
      blankIcon.src = this.icons.blankWhite;
      blankIcon.style.cssText = 'height: 5px; width: 5px;';
      rightSide.append(document.createElement('br'), blankIcon);
      header.append(rightSide);
      popupContent.append(header);

      if (place.photos.length) {
        let photoIndex = 0;
        const photoContainer = document.createElement('div');
        const photoAlignment = document.createElement('span');
        photoAlignment.style.textAlign = 'center';
        const image = document.createElement('img');
        image.className = window.isDesktop ? 'popupimgbig' : 'popupimgsmall';
        image.style.cursor = 'pointer';
        image.addEventListener('click', (event) => this.openPhotoGallery(place, photoIndex, event));
        image.addEventListener('load', () => {
          (window.mainmap as L.Map & { _popup?: L.Popup })._popup?.update();
        });
        let photoInfo: HTMLDivElement | null = null;
        const updatePhotoInfo = () => {
          if (!photoInfo) return;
          const photo = place.photos[photoIndex];
          const date = this.formatDateSeconds(photo.exif_time_taken);
          photoInfo.textContent = [photo.org_filename, date].filter((value): value is string => Boolean(value)).join(' ');
          photoInfo.hidden = !photoInfo.textContent;
        };

        const setPhoto = () => {
          const photo = place.photos[photoIndex];
          image.src = photo.get_image_thumb() || photo.image_thumb;
          updatePhotoInfo();
        };

        if (place.photos.length > 1) {
          const table = document.createElement('table');
          table.style.maxWidth = '100%';
          const row = document.createElement('tr');
          const previousCell = document.createElement('td');
          const imageCell = document.createElement('td');
          const nextCell = document.createElement('td');
          previousCell.style.padding = '0px';
          imageCell.style.cssText = 'padding-left: 5px; padding-right: 5px;';
          nextCell.style.padding = '0px';
          image.className = window.isDesktop ? 'popupimgbigmorephotos' : 'popupimgsmall';

          const previousImage = document.createElement('img');
          const nextImage = document.createElement('img');
          const setArrowState = () => {
            previousImage.src = photoIndex === 0 ? this.icons.arrowLeftDisabled : this.icons.arrowLeft;
            previousImage.style.cursor = photoIndex === 0 ? 'arrow' : 'pointer';
            nextImage.src = photoIndex === place.photos.length - 1 ? this.icons.arrowRightDisabled : this.icons.arrowRight;
            nextImage.style.cursor = photoIndex === place.photos.length - 1 ? 'arrow' : 'pointer';
          };
          previousImage.style.cssText = 'width: 10px; height: 20px;';
          nextImage.style.cssText = 'width: 10px; height: 20px;';
          previousImage.addEventListener('click', () => {
            if (photoIndex > 0) {
              photoIndex -= 1;
              setPhoto();
              setArrowState();
            }
          });
          nextImage.addEventListener('click', () => {
            if (photoIndex < place.photos.length - 1) {
              photoIndex += 1;
              setPhoto();
              setArrowState();
            }
          });
          setArrowState();
          previousCell.append(previousImage);
          imageCell.append(image);
          nextCell.append(nextImage);
          row.append(previousCell, imageCell, nextCell);
          table.append(row);
          photoAlignment.append(table);
        } else {
          photoAlignment.append(image);
        }

        image.style.maxWidth = '100%';
        setPhoto();
        photoContainer.append(photoAlignment);
        if (window.isDesktop) {
          photoInfo = document.createElement('div');
          photoInfo.style.cssText = 'display: block; clear: both; text-align: right; color: gray; overflow-wrap: anywhere;';
          updatePhotoInfo();
          photoContainer.append(photoInfo);
        }
        popupContent.append(photoContainer);
      }

      return popupContent;
    },
    openPhotoGallery(place: CzasWLasPopupPlace, photoIndex: number, event: MouseEvent) {
      const useOriginal = event.shiftKey || event.ctrlKey || event.altKey;
      const photos = place.photos.map((photo) => ({
        src: useOriginal ? photo.image : photo.get_image_fullhd(),
        thumb: photo.get_image_thumb(),
      }));
      const gallery = lightGallery(document.getElementById('map'), {
        dynamic: true,
        dynamicEl: photos,
        plugins: [lgZoom, lgAutoplay],
        showZoomInOutIcons: true,
        slideShowInterval: 3000,
        index: photoIndex,
        allowMediaOverlap: true,
        mobileSettings: {
          controls: false,
          showCloseIcon: true,
          download: false,
        },
      });
      gallery.openGallery();
    },
    updateCzasWLasLayer() {
      if (!this.deckOverlay && !this.czaswlasLayer) return;
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;
      if (this.czaswlasLayer) {
        this.czaswlasLayer.setVisible(this.checkedAll);
      }
      this.deckOverlay.setProps({ layers: this.czaswlasLayer ? [this.czaswlasLayer.getLayer()] : [] });
    },
    getStatusFilterRange(): readonly [number, number] {
      if (this.active && this.inactive) return [0, 1];
      if (this.active) return [1, 1];
      if (this.inactive) return [0, 0];
      return [2, 2];
    },
    onStatusFilterChanged() {
      if (!this.czaswlasLayer) return;
      this.czaswlasLayer.setFilterRange(this.getStatusFilterRange());
      this.updateCzasWLasLayer();
    },
    createCzasWLasLayer() {
      if (!this.czaswlasResult) return;
      const preparedData = this.createBinaryData(this.czaswlasResult);
      this.czaswlasLayerResults = preparedData.results;
      this.czaswlasLayer = new MapLasScatterplotLayer({
        id: 'czaswlas-points',
        data: preparedData.data,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getStatusFilterRange(),
        positionFormat: 'XY',
        _normalize: false,
        getRadius: 8,
        radiusUnits: 'pixels',
        stroked: true,
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        visible: this.checkedAll,
        pickable: false,
      });

    },
    onCzasWLasCheckboxChanged() {
      if (this.checkedAll && !this.czaswlasLayer) {
        this.createCzasWLasLayer();
      }
      if (!this.checkedAll) this.clearHoveredWaypoints();
      if (!this.checkedAll && window.mainmap) {
        window.mainmap.getContainer().classList.remove('czaswlas-pointer-cursor');
      }
      this.updateCzasWLasLayer();
    },
    onSelectedAreaChanged() {
      this.clearHoveredWaypoints();
      if (this.checkedAll && this.czaswlasResult) {
        this.createCzasWLasLayer();
      } else {
        this.czaswlasLayer = null;
        this.czaswlasLayerResults = [];
      }
      if (this.checkedAll) {
        this.updateCzasWLasLayer();
      }
    },
    downloadPlacesCzasWLas() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/czaswlas/';
      this.downloadUrlWithCache(endPoint, 'czaswlas', 'czaswlas.txt', null, true).then((response) => {
        this.czaswlasResult = response[1].results;
        this.loaded = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
  },
  watch: {
    checkedAll() {
      this.onCzasWLasCheckboxChanged();
    },
    active() {
      this.onStatusFilterChanged();
    },
    inactive() {
      this.onStatusFilterChanged();
    },
  }
})


</script>